import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Base API functions
const fetchExercisesApi = async (workoutId) => {
  const response = await axios.get(`/workouts/${workoutId}/exercises.json`);
  return response.data;
};

const fetchExerciseTemplatesApi = async () => {
  const response = await axios.get("/exercise_templates/list.json");
  return response.data;
};

const createExerciseApi = async ({
  workoutId,
  exerciseTemplateId,
  reps,
  weight,
}) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  const response = await axios.post(
    `/workouts/${workoutId}/exercises.json`,
    {
      exercise: {
        exercise_template_id: exerciseTemplateId,
        reps,
        weight,
      },
    },
    {
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

const updateExerciseApi = async ({
  workoutId,
  exerciseId,
  exerciseTemplateId,
}) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  const response = await axios.patch(
    `/workouts/${workoutId}/exercises/${exerciseId}.json`,
    { exercise: { exercise_template_id: exerciseTemplateId } },
    {
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

const deleteExerciseApi = async ({ workoutId, exerciseId }) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  await axios.delete(`/workouts/${workoutId}/exercises/${exerciseId}.json`, {
    headers: {
      "X-CSRF-Token": csrfToken,
    },
  });
  return { success: true };
};

const updateExercisePositionApi = async ({ exerciseId, newPosition }) => {
  const csrfToken = document.querySelector('meta[name="csrf-token"]').content;
  const response = await axios.patch(
    "/exercises/update_position",
    { exercise: { id: exerciseId, position: newPosition } },
    {
      headers: {
        "X-CSRF-Token": csrfToken,
        "Content-Type": "application/json",
      },
    }
  );
  return response.data;
};

const fetchExerciseHistoryApi = async (exerciseTemplateId) => {
  const response = await axios.get(
    `/exercise_history/${exerciseTemplateId}.json`
  );
  return response.data;
};

// React Query hooks
export function useExercises(workoutId) {
  return useQuery({
    queryKey: ["exercises", workoutId],
    queryFn: () => fetchExercisesApi(workoutId),
    enabled: !!workoutId,
  });
}

export function useExerciseTemplates() {
  return useQuery({
    queryKey: ["exerciseTemplates"],
    queryFn: fetchExerciseTemplatesApi,
  });
}

export function useCreateExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createExerciseApi,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["exercises", variables.workoutId],
      });
    },
  });
}

export function useUpdateExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExerciseApi,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["exercises", variables.workoutId],
      });
    },
  });
}

export function useDeleteExercise() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteExerciseApi,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["exercises", variables.workoutId],
      });
    },
  });
}

export function useUpdateExercisePosition() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateExercisePositionApi,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["exercises"] });
    },
  });
}

export function useExerciseHistory(exerciseTemplateId) {
  return useQuery({
    queryKey: ["exerciseHistory", exerciseTemplateId],
    queryFn: () => fetchExerciseHistoryApi(exerciseTemplateId),
    enabled: !!exerciseTemplateId,
  });
}
